import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Leaf } from "@/components/Botanical";

function formatDetail(d) {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return String(d);
}

const inputCls =
  "w-full bg-white border border-[#E5E7E3] px-4 py-2.5 text-sm text-[#0A0A0A] focus:outline-none focus:border-[#11261A] transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#8EA696] mb-1.5";

function Field({ label, value, onChange, textarea = false, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea
          className={`${inputCls} min-h-[90px]`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : (
        <input
          className={inputCls}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      )}
    </div>
  );
}

function move(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

function ReorderBtns({ onUp, onDown, onDelete, prefix }) {
  const btn = "px-2.5 py-1.5 border border-[#E5E7E3] text-xs text-[#4A4A4A] hover:border-[#11261A] hover:text-[#11261A] transition-colors";
  return (
    <div className="flex gap-1.5 shrink-0">
      <button type="button" className={btn} onClick={onUp} data-testid={`${prefix}-up`} aria-label="Move up">↑</button>
      <button type="button" className={btn} onClick={onDown} data-testid={`${prefix}-down`} aria-label="Move down">↓</button>
      {onDelete && (
        <button type="button" className={`${btn} text-red-700 hover:border-red-700`} onClick={onDelete} data-testid={`${prefix}-delete`} aria-label="Delete">✕</button>
      )}
    </div>
  );
}

function useEditor(key, token) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setData(null);
    axios.get(`${API}/content/${key}`).then((r) => setData(r.data)).catch(() => setStatus("Could not load content"));
  }, [key]);
  const save = async () => {
    setStatus("Saving…");
    try {
      await axios.put(`${API}/content/${key}`, data, { headers: { Authorization: `Bearer ${token}` } });
      setStatus("Saved — live on the site");
    } catch (e) {
      setStatus(formatDetail(e.response?.data?.detail));
    }
    setTimeout(() => setStatus(""), 3500);
  };
  return { data, setData, status, save };
}

function SaveBar({ status, onSave, k }) {
  return (
    <div className="sticky bottom-0 bg-[#F9FAF8]/95 backdrop-blur border-t border-[#E5E7E3] -mx-6 md:-mx-10 px-6 md:px-10 py-4 mt-10 flex items-center justify-between gap-4">
      <p className="text-xs text-[#4A4A4A]" data-testid={`admin-status-${k}`}>{status}</p>
      <button
        type="button"
        onClick={onSave}
        data-testid={`admin-save-${k}`}
        className="rounded-full bg-[#11261A] text-[#F9FAF8] text-[11px] uppercase tracking-[0.2em] px-7 py-3 hover:bg-black transition-colors"
      >
        Save changes
      </button>
    </div>
  );
}

function UploadButton({ token, accept, onUploaded, label, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) {
      setErr("That file is over 100 MB — please compress it first (a phone trim or any compressor works)");
      e.target.value = "";
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000,
      });
      onUploaded(data);
    } catch (err2) {
      setErr(formatDetail(err2.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <label
        className={`cursor-pointer rounded-full border border-[#1B3B28]/40 text-[#1B3B28] text-[11px] uppercase tracking-[0.18em] px-4 py-2 transition-colors hover:bg-[#11261A] hover:text-[#F9FAF8] ${busy ? "opacity-60 pointer-events-none" : ""}`}
        data-testid={testid}
      >
        {busy ? "Uploading…" : label}
        <input type="file" accept={accept} className="hidden" onChange={pick} disabled={busy} />
      </label>
      {err && <span className="text-xs text-red-700">{err}</span>}
    </span>
  );
}

function SettingsEditor({ token }) {
  const { data, setData, status, save } = useEditor("settings", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const set = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Salon name" value={data.name} onChange={set("name")} testid="settings-name" />
        <Field label="Tagline" value={data.tagline} onChange={set("tagline")} testid="settings-tagline" />
        <Field label="Phone (display)" value={data.phone} onChange={set("phone")} testid="settings-phone" />
        <Field label="Phone (dialling, country code)" value={data.phone_raw} onChange={set("phone_raw")} testid="settings-phone-raw" />
        <Field label="E-mail" value={data.email} onChange={set("email")} testid="settings-email" />
        <Field label="Hours" value={data.hours} onChange={set("hours")} testid="settings-hours" />
        <div className="md:col-span-2"><Field label="Address" value={data.address} onChange={set("address")} testid="settings-address" /></div>
        <div className="md:col-span-2"><Field label="Booking link" value={data.booking_url} onChange={set("booking_url")} testid="settings-booking" /></div>
      </div>
      <SaveBar status={status} onSave={save} k="settings" />
    </div>
  );
}

function HomeEditor({ token }) {
  const { data, setData, status, save } = useEditor("home", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const set = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Eyebrow (small text above)" value={data.eyebrow} onChange={set("eyebrow")} testid="home-eyebrow" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Line 1" value={data.line1} onChange={set("line1")} testid="home-line1" />
          <Field label="Line 2" value={data.line2} onChange={set("line2")} testid="home-line2" />
          <Field label="Line 3" value={data.line3} onChange={set("line3")} testid="home-line3" />
        </div>
        <div className="md:col-span-2"><Field label="Intro paragraph" value={data.subtext} onChange={set("subtext")} textarea testid="home-subtext" /></div>
      </div>
      <SaveBar status={status} onSave={save} k="home" />
    </div>
  );
}

function ServicesEditor({ token }) {
  const { data, setData, status, save } = useEditor("services", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const cats = data.categories || [];
  const setCats = (c) => setData({ ...data, categories: c });
  const setItem = (ci, si, ii, k, v) => {
    const c = JSON.parse(JSON.stringify(cats));
    c[ci].sections[si].items[ii][k] = v;
    setCats(c);
  };
  return (
    <div>
      <Field label="Note under the menu" value={data.note} onChange={(v) => setData({ ...data, note: v })} textarea testid="services-note-edit" />
      <div className="mt-8 space-y-8">
        {cats.map((cat, ci) => (
          <div key={ci} className="border border-[#E5E7E3] bg-white p-5" data-testid={`editor-category-${ci}`}>
            <div className="flex items-center justify-between gap-3 mb-4">
              <input className={`${inputCls} font-display text-lg`} value={cat.category}
                onChange={(e) => { const c = [...cats]; c[ci] = { ...c[ci], category: e.target.value }; setCats(c); }}
                data-testid={`editor-category-name-${ci}`} />
              <ReorderBtns prefix={`cat-${ci}`}
                onUp={() => setCats(move(cats, ci, -1))}
                onDown={() => setCats(move(cats, ci, 1))}
                onDelete={() => setCats(cats.filter((_, i) => i !== ci))} />
            </div>
            <Field label="Blurb" value={cat.blurb || ""} onChange={(v) => { const c = [...cats]; c[ci] = { ...c[ci], blurb: v }; setCats(c); }} testid={`editor-category-blurb-${ci}`} />
            {(cat.sections || []).map((sec, si) => (
              <div key={si} className="mt-5 border-t border-[#E5E7E3] pt-4">
                <input className={`${inputCls} mb-3`} value={sec.title}
                  onChange={(e) => { const c = JSON.parse(JSON.stringify(cats)); c[ci].sections[si].title = e.target.value; setCats(c); }}
                  data-testid={`editor-section-title-${ci}-${si}`} />
                <div className="space-y-2">
                  {sec.items.map((item, ii) => (
                    <div key={ii} className="grid grid-cols-12 gap-2 items-center">
                      <input className={`${inputCls} col-span-5`} placeholder="Service name" value={item.name}
                        onChange={(e) => setItem(ci, si, ii, "name", e.target.value)} data-testid={`editor-item-name-${ci}-${si}-${ii}`} />
                      <input className={`${inputCls} col-span-2`} placeholder="Member ₹" value={item.member || ""}
                        onChange={(e) => setItem(ci, si, ii, "member", e.target.value)} data-testid={`editor-item-member-${ci}-${si}-${ii}`} />
                      <input className={`${inputCls} col-span-2`} placeholder="Standard ₹" value={item.standard || ""}
                        onChange={(e) => setItem(ci, si, ii, "standard", e.target.value)} data-testid={`editor-item-standard-${ci}-${si}-${ii}`} />
                      <input className={`${inputCls} col-span-2`} placeholder="Note" value={item.note || ""}
                        onChange={(e) => setItem(ci, si, ii, "note", e.target.value)} data-testid={`editor-item-note-${ci}-${si}-${ii}`} />
                      <div className="col-span-1 flex justify-end">
                        <ReorderBtns prefix={`item-${ci}-${si}-${ii}`}
                          onUp={() => { const c = JSON.parse(JSON.stringify(cats)); c[ci].sections[si].items = move(c[ci].sections[si].items, ii, -1); setCats(c); }}
                          onDown={() => { const c = JSON.parse(JSON.stringify(cats)); c[ci].sections[si].items = move(c[ci].sections[si].items, ii, 1); setCats(c); }}
                          onDelete={() => { const c = JSON.parse(JSON.stringify(cats)); c[ci].sections[si].items.splice(ii, 1); setCats(c); }} />
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button"
                  className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#1B3B28] border border-dashed border-[#1B3B28]/40 px-4 py-2 hover:bg-[#F9FAF8]"
                  onClick={() => { const c = JSON.parse(JSON.stringify(cats)); c[ci].sections[si].items.push({ name: "", member: "", standard: "", note: "" }); setCats(c); }}
                  data-testid={`editor-add-item-${ci}-${si}`}>
                  + Add service
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
      <SaveBar status={status} onSave={save} k="services" />
    </div>
  );
}

const EMPTY_BRAND = { brand: "", origin: "", tags: [], product: "", product_blurb: "", alignment: "", image: "" };

function BrandsEditor({ token }) {
  const { data, setData, status, save } = useEditor("brands", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const brands = data;
  const setB = (i, k, v) => { const b = brands.map((x, j) => (j === i ? { ...x, [k]: v } : x)); setData(b); };
  return (
    <div>
      <p className="text-xs text-[#4A4A4A] mb-6">The daily spotlight rotates through this list — one brand per day, in order.</p>
      <div className="space-y-6">
        {brands.map((b, i) => (
          <div key={i} className="border border-[#E5E7E3] bg-white p-5" data-testid={`editor-brand-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-display text-xl text-[#11261A]">{b.brand || `Brand ${i + 1}`}</p>
              <ReorderBtns prefix={`brand-${i}`}
                onUp={() => setData(move(brands, i, -1))}
                onDown={() => setData(move(brands, i, 1))}
                onDelete={() => setData(brands.filter((_, j) => j !== i))} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Brand" value={b.brand} onChange={(v) => setB(i, "brand", v)} testid={`editor-brand-name-${i}`} />
              <Field label="Origin" value={b.origin} onChange={(v) => setB(i, "origin", v)} testid={`editor-brand-origin-${i}`} />
              <div className="md:col-span-2"><Field label="USP tags (comma separated)" value={(b.tags || []).join(", ")} onChange={(v) => setB(i, "tags", v.split(",").map((t) => t.trim()).filter(Boolean))} testid={`editor-brand-tags-${i}`} /></div>
              <div className="md:col-span-2"><Field label="Product" value={b.product} onChange={(v) => setB(i, "product", v)} testid={`editor-brand-product-${i}`} /></div>
              <Field label="Product image (path or URL — optional)" value={b.image || ""} onChange={(v) => setB(i, "image", v)} testid={`editor-brand-image-${i}`} />
              <div className="flex items-end pb-1">
                <UploadButton token={token} accept="image/*" label="Upload image" testid={`editor-brand-upload-${i}`} onUploaded={(d) => setB(i, "image", d.url)} />
              </div>
              <div className="md:col-span-2"><Field label="Product description" value={b.product_blurb} onChange={(v) => setB(i, "product_blurb", v)} textarea testid={`editor-brand-blurb-${i}`} /></div>
              <div className="md:col-span-2"><Field label="Why it aligns with Orgænics" value={b.alignment} onChange={(v) => setB(i, "alignment", v)} textarea testid={`editor-brand-alignment-${i}`} /></div>
            </div>
          </div>
        ))}
      </div>
      <button type="button"
        className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[#1B3B28] border border-dashed border-[#1B3B28]/40 px-5 py-3 hover:bg-white"
        onClick={() => setData([...brands, { ...EMPTY_BRAND }])}
        data-testid="editor-add-brand">
        + Add brand
      </button>
      <SaveBar status={status} onSave={save} k="brands" />
    </div>
  );
}

function MembershipsEditor({ token }) {
  const { data, setData, status, save } = useEditor("memberships", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const setPlan = (id, k, v) => setData({ ...data, plans: { ...data.plans, [id]: { ...data.plans[id], [k]: v } } });
  const setClaimed = (id, v) => setData({ ...data, claimed: { ...data.claimed, [id]: Math.max(0, parseInt(v || "0", 10) || 0) } });
  return (
    <div>
      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <Field label="Total founding seats" value={String(data.total_seats)} onChange={(v) => setData({ ...data, total_seats: parseInt(v || "0", 10) || 50 })} testid="memberships-total" />
        <Field label="Essentials — seats claimed" value={String(data.claimed?.essentials ?? 0)} onChange={(v) => setClaimed("essentials", v)} testid="memberships-claimed-essentials" />
        <Field label="Family — seats claimed" value={String(data.claimed?.family ?? 0)} onChange={(v) => setClaimed("family", v)} testid="memberships-claimed-family" />
      </div>
      {["essentials", "family"].map((id) => {
        const p = data.plans[id];
        return (
          <div key={id} className="border border-[#E5E7E3] bg-white p-5 mb-6" data-testid={`editor-plan-${id}`}>
            <p className="font-display text-xl text-[#11261A] mb-4 capitalize">{id} plan</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Plan name" value={p.name} onChange={(v) => setPlan(id, "name", v)} testid={`plan-${id}-name`} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Original price (struck)" value={p.original_price} onChange={(v) => setPlan(id, "original_price", v)} testid={`plan-${id}-original`} />
                <Field label="Founding price" value={p.price} onChange={(v) => setPlan(id, "price", v)} testid={`plan-${id}-price`} />
              </div>
              <div className="md:col-span-2"><Field label="Description" value={p.blurb} onChange={(v) => setPlan(id, "blurb", v)} textarea testid={`plan-${id}-blurb`} /></div>
              <div className="md:col-span-2"><Field label="Benefits (one per line)" value={(p.features || []).join("\n")} onChange={(v) => setPlan(id, "features", v.split("\n").filter((x) => x.trim()))} textarea testid={`plan-${id}-features`} /></div>
            </div>
          </div>
        );
      })}
      <SaveBar status={status} onSave={save} k="memberships" />
    </div>
  );
}

function ListEditor({ items, onChange, fields, prefix, addLabel, empty }) {
  return (
    <div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-3 border border-[#E5E7E3] bg-white p-3" data-testid={`${prefix}-row-${i}`}>
            <div className="flex-1 grid md:grid-cols-3 gap-2">
              {fields.map((f) => (
                <input key={f.key} className={inputCls} placeholder={f.label} value={item[f.key] ?? ""}
                  onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [f.key]: e.target.value } : x)))}
                  data-testid={`${prefix}-${f.key}-${i}`} />
              ))}
            </div>
            <ReorderBtns prefix={`${prefix}-${i}`}
              onUp={() => onChange(move(items, i, -1))}
              onDown={() => onChange(move(items, i, 1))}
              onDelete={() => onChange(items.filter((_, j) => j !== i))} />
          </div>
        ))}
      </div>
      <button type="button"
        className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#1B3B28] border border-dashed border-[#1B3B28]/40 px-4 py-2 hover:bg-white"
        onClick={() => onChange([...items, { ...empty }])}
        data-testid={`${prefix}-add`}>
        {addLabel}
      </button>
    </div>
  );
}

function GalleryEditor({ token }) {
  const { data, setData, status, save } = useEditor("gallery", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  return (
    <div className="space-y-10">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Hero image (path or URL)" value={data.hero_image} onChange={(v) => setData({ ...data, hero_image: v })} testid="gallery-hero" />
        <Field label="Story image" value={data.story_image} onChange={(v) => setData({ ...data, story_image: v })} testid="gallery-story" />
      </div>
      <div className="border border-dashed border-[#1B3B28]/40 bg-white p-5" data-testid="gallery-uploads">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8EA696] mb-3">Upload new files — they land at the end of the list; move them with the arrows, then Save changes</p>
        <div className="flex flex-wrap gap-3">
          <UploadButton token={token} accept="image/*" label="Upload → hero image" testid="upload-hero" onUploaded={(d) => setData({ ...data, hero_image: d.url })} />
          <UploadButton token={token} accept="image/*" label="Upload → story image" testid="upload-story" onUploaded={(d) => setData({ ...data, story_image: d.url })} />
          <UploadButton token={token} accept="image/*" label="Upload → photo strip" testid="upload-strip" onUploaded={(d) => setData({ ...data, marquee: [...(data.marquee || []), { src: d.url, alt: "" }] })} />
          <UploadButton token={token} accept="video/*" label="Upload → gallery film" testid="upload-video" onUploaded={(d) => setData({ ...data, videos: [...(data.videos || []), { src: d.url, poster: d.poster_url || "", label: "" }] })} />
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#1B3B28] font-semibold mb-3">Scrolling photo strip</p>
        <ListEditor items={data.marquee || []} onChange={(m) => setData({ ...data, marquee: m })} prefix="marquee"
          fields={[{ key: "src", label: "Image path / URL" }, { key: "alt", label: "Caption" }]}
          addLabel="+ Add photo" empty={{ src: "", alt: "" }} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#1B3B28] font-semibold mb-3">Gallery films</p>
        <ListEditor items={data.videos || []} onChange={(v) => setData({ ...data, videos: v })} prefix="video"
          fields={[{ key: "src", label: "Video path" }, { key: "poster", label: "Poster path" }, { key: "label", label: "Label" }]}
          addLabel="+ Add film" empty={{ src: "", poster: "", label: "" }} />
      </div>
      <SaveBar status={status} onSave={save} k="gallery" />
    </div>
  );
}

const BANNER_IDEAS = [
  { tag: "Diwali", text: "Festive glow rituals — book your Diwali appointments early", label: "Book now" },
  { tag: "New Year", text: "New year, naturally — begin with conscious care", label: "Reserve a chair" },
  { tag: "Anniversary", text: "Our anniversary week — member rates for every guest", label: "Celebrate with us" },
  { tag: "Wedding season", text: "Bridal season is here — reserve your bridal consultation", label: "Plan my look" },
  { tag: "Monsoon", text: "Monsoon frizz? Our repair rituals have you covered", label: "Book a ritual" },
  { tag: "Summer", text: "Summer scalp detox — cool, cleanse, restore", label: "Try it" },
  { tag: "Valentine's", text: "Self-love season — gift yourself a ritual", label: "Treat yourself" },
  { tag: "Membership drive", text: "Founding memberships now open — first 50 enrolments only", label: "Claim yours" },
];

function BannerEditor({ token }) {
  const { data, setData, status, save } = useEditor("banner", token);
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  return (
    <div className="space-y-6 max-w-2xl">
      <label className="flex items-center gap-4 border border-[#E5E7E3] bg-white px-5 py-4 cursor-pointer" data-testid="banner-enabled-toggle">
        <input
          type="checkbox"
          checked={!!data.enabled}
          onChange={(e) => setData({ ...data, enabled: e.target.checked })}
          className="w-5 h-5 accent-[#11261A]"
        />
        <span>
          <span className="block text-sm text-[#0A0A0A]">Show the announcement bar</span>
          <span className="block text-xs text-[#4A4A4A] mt-0.5">A slim dark-green bar appears at the very top of every page</span>
        </span>
      </label>
      <Field label="Banner text" value={data.text} onChange={(v) => setData({ ...data, text: v })} testid="banner-text" />
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Button label" value={data.link_label} onChange={(v) => setData({ ...data, link_label: v })} testid="banner-link-label" />
        <Field label="Button link" value={data.link} onChange={(v) => setData({ ...data, link: v })} testid="banner-link" />
      </div>
      <p className="text-xs text-[#4A4A4A]">Leave the button link empty to show text only.</p>
      <div className="border border-[#E5E7E3] bg-white p-5" data-testid="banner-ideas">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8EA696] mb-3">Ready-to-paste seasonal ideas — tap to use</p>
        <div className="flex flex-wrap gap-2">
          {BANNER_IDEAS.map((idea) => (
            <button
              key={idea.tag}
              type="button"
              title={idea.text}
              onClick={() => setData({ ...data, text: idea.text, link_label: idea.label })}
              data-testid={`banner-idea-${idea.tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-full border border-[#1B3B28]/25 text-[#1B3B28] text-[11px] uppercase tracking-[0.15em] px-4 py-2 hover:bg-[#11261A] hover:text-[#F9FAF8] hover:border-[#11261A] transition-colors"
            >
              {idea.tag}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-[#4A4A4A] mt-3">Hover any tag to preview the full line before you tap.</p>
      </div>
      <SaveBar status={status} onSave={save} k="banner" />
    </div>
  );
}

const COVER_OPTIONS = [
  { id: "none", label: "Off" },
  { id: "diwali", label: "Diwali" },
  { id: "newyear", label: "New Year" },
  { id: "holi", label: "Holi" },
  { id: "anniversary", label: "Anniversary" },
  { id: "custom", label: "Custom image" },
];

const COVER_BANNERS = {
  diwali: { text: "Diwali Glow Week — festive rituals at celebratory prices · limited evening slots", link_label: "Reserve your slot" },
  newyear: { text: "New Year, New Ritual — begin the year with hair that feels like you", link_label: "Book January" },
  holi: { text: "Holi-ready hair — pre & post colour care rituals all week", link_label: "Book your ritual" },
  anniversary: { text: "Our anniversary week — thank-you prices on signature rituals", link_label: "Celebrate with us" },
};

function CoverEditor({ token }) {
  const { data, setData, status, save } = useEditor("spotlight_cover", token);
  const [pairMsg, setPairMsg] = useState("");
  if (!data) return <p className="text-sm text-[#4A4A4A]">Loading…</p>;
  const pairBanner = async () => {
    const preset = COVER_BANNERS[data.active];
    if (!preset) return;
    setPairMsg("Switching on…");
    try {
      const cur = (await axios.get(`${API}/content/banner`)).data;
      await axios.put(
        `${API}/content/banner`,
        { ...cur, ...preset, enabled: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPairMsg("Offer banner is on — it shows at the very top of every page");
    } catch {
      setPairMsg("Could not update the banner — try the Offer banner tab");
    }
    setTimeout(() => setPairMsg(""), 4000);
  };
  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#1B3B28] font-semibold mb-3">Festive frame for the daily spotlight</p>
        <div className="flex flex-wrap gap-2" data-testid="cover-options">
          {COVER_OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setData({ ...data, active: o.id })}
              data-testid={`cover-option-${o.id}`}
              className={`rounded-full border text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 transition-colors ${
                data.active === o.id
                  ? "bg-[#11261A] text-[#F9FAF8] border-[#11261A]"
                  : "border-[#1B3B28]/30 text-[#1B3B28] hover:bg-white"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#4A4A4A] mt-3">
          Diwali drapes a marigold toran with glowing diyas; New Year adds floating gold sparkles; Holi drifts soft gulal powder puffs; Anniversary rains gold confetti; Custom shows your uploaded frame image across the top of the spotlight.
        </p>
      </div>
      <Field
        label="Greeting line (shown above the spotlight, e.g. “Happy Diwali — may your season glow”)"
        value={data.greeting}
        onChange={(v) => setData({ ...data, greeting: v })}
        testid="cover-greeting"
      />
      {data.active === "custom" && (
        <div className="border border-dashed border-[#1B3B28]/40 bg-white p-5" data-testid="cover-custom-upload">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8EA696] mb-3">
            Upload a decorative frame image (transparent PNG works best — garlands, lanterns, streamers)
          </p>
          <UploadButton
            token={token}
            accept="image/*"
            label="Upload frame image"
            testid="cover-upload"
            onUploaded={(d) => setData({ ...data, custom_image: d.url })}
          />
          {data.custom_image && (
            <div className="mt-4 flex items-center gap-4">
              <img src={data.custom_image} alt="Custom frame" className="h-20 w-auto border border-[#E5E7E3]" data-testid="cover-custom-preview" />
              <button
                type="button"
                onClick={() => setData({ ...data, custom_image: "" })}
                className="text-[11px] uppercase tracking-[0.18em] text-red-700 border-b border-red-300"
                data-testid="cover-custom-remove"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}
      {COVER_BANNERS[data.active] && (
        <div className="border border-[#E8C87A]/60 bg-[#FDF9EF] p-5" data-testid="cover-banner-pair">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#B9975B] mb-2">Pair the festive offer banner</p>
          <p className="font-display italic text-lg text-[#11261A] mb-3">“{COVER_BANNERS[data.active].text}”</p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={pairBanner}
              data-testid="cover-pair-banner"
              className="rounded-full bg-[#11261A] text-[#F9FAF8] text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-black transition-colors"
            >
              Switch on this banner too
            </button>
            <span className="text-xs text-[#4A4A4A]" data-testid="cover-pair-status">{pairMsg}</span>
          </div>
          <p className="text-[11px] text-[#4A4A4A] mt-3">Turns on the slim announcement bar with this ready-made line. Switch it off anytime in the Offer banner tab.</p>
        </div>
      )}
      <SaveBar status={status} onSave={save} k="spotlight_cover" />
    </div>
  );
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function MediaLibrary({ token }) {
  const [files, setFiles] = useState(null);
  const [copied, setCopied] = useState("");
  const [confirmId, setConfirmId] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    axios
      .get(`${API}/files`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setFiles(r.data))
      .catch(() => setErr("Could not load the media library"));
  };
  useEffect(load, [token]);

  const copy = async (f) => {
    const full = `${window.location.origin}${f.url}`;
    try {
      await navigator.clipboard.writeText(full);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = full;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(f.id);
    setTimeout(() => setCopied(""), 2000);
  };

  const del = async (f) => {
    try {
      await axios.delete(`${API}/files/${f.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      setConfirmId("");
    } catch {
      setErr("Delete failed — please try again");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-[#4A4A4A] max-w-xl">
          Every photo and video you have uploaded lives here. Copy a link to reuse a file anywhere, or delete files you no longer need.
          Deleting removes the file site-wide — anything still using it will show its fallback.
        </p>
        <UploadButton token={token} accept="image/*,video/*" label="Upload new file" testid="library-upload" onUploaded={load} />
      </div>
      {err && <p className="text-sm text-red-700" data-testid="library-error">{err}</p>}
      {!files ? (
        <p className="text-sm text-[#4A4A4A]">Loading…</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-[#4A4A4A]" data-testid="library-empty">Nothing uploaded yet — use the button above to add your first file.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-library-grid">
          {files.map((f) => (
            <div key={f.id} className="border border-[#E5E7E3] bg-white group" data-testid={`media-item-${f.id}`}>
              <div className="aspect-square bg-[#F9FAF8] flex items-center justify-center overflow-hidden">
                {f.content_type?.startsWith("image/") ? (
                  <img src={f.url} alt={f.filename} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <video src={f.url} className="w-full h-full object-cover" muted preload="metadata" />
                )}
              </div>
              <div className="p-3">
                <p className="text-xs text-[#0A0A0A] truncate" title={f.filename}>{f.filename}</p>
                <p className="text-[10px] text-[#4A4A4A] mt-0.5">
                  {fmtSize(f.size)}{f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => copy(f)}
                    data-testid={`media-copy-${f.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] text-[#1B3B28] border-b border-[#1B3B28]/30 hover:border-[#1B3B28]"
                  >
                    {copied === f.id ? "Copied" : "Copy link"}
                  </button>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-[0.15em] text-[#1B3B28] border-b border-[#1B3B28]/30 hover:border-[#1B3B28]"
                    data-testid={`media-open-${f.id}`}
                  >
                    Open
                  </a>
                  {confirmId === f.id ? (
                    <button
                      type="button"
                      onClick={() => del(f)}
                      data-testid={`media-confirm-delete-${f.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] text-red-700 font-semibold border-b border-red-700"
                    >
                      Sure?
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setConfirmId(f.id); setTimeout(() => setConfirmId(""), 3000); }}
                      data-testid={`media-delete-${f.id}`}
                      className="text-[10px] uppercase tracking-[0.15em] text-red-700 border-b border-red-300 hover:border-red-700"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: "settings", label: "Site settings", El: SettingsEditor },
  { id: "home", label: "Home hero", El: HomeEditor },
  { id: "services", label: "Services & prices", El: ServicesEditor },
  { id: "brands", label: "Brand spotlight", El: BrandsEditor },
  { id: "memberships", label: "Memberships", El: MembershipsEditor },
  { id: "gallery", label: "Photos & films", El: GalleryEditor },
  { id: "banner", label: "Offer banner", El: BannerEditor },
  { id: "cover", label: "Spotlight covers", El: CoverEditor },
  { id: "library", label: "Media library", El: MediaLibrary },
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("so_admin_token") || "");
  const [checking, setChecking] = useState(!!token);
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("settings");

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthed(true))
      .catch(() => {
        sessionStorage.removeItem("so_admin_token");
        setToken("");
      })
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { login_id: loginId, password });
      sessionStorage.setItem("so_admin_token", data.token);
      setToken(data.token);
      setAuthed(true);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("so_admin_token");
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9FAF8] text-sm text-[#4A4A4A]">Checking session…</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#11261A] grain relative px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm bg-[#F9FAF8] p-10 relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <Leaf className="w-8 h-8" />
            <div>
              <p className="font-display text-lg tracking-[0.15em] text-[#11261A]">SALON ORGÆNICS</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">Admin console</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className={labelCls}>Login ID</label>
              <input className={inputCls} value={loginId} onChange={(e) => setLoginId(e.target.value)} autoComplete="username" data-testid="admin-login-id-input" />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="admin-password-input" />
            </div>
            {error && <p className="text-xs text-red-700" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="admin-login-submit"
              className="w-full rounded-full bg-[#11261A] text-[#F9FAF8] text-[11px] uppercase tracking-[0.25em] py-3.5 hover:bg-black transition-colors disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const Active = TABS.find((t) => t.id === tab).El;
  return (
    <div className="min-h-screen bg-[#F9FAF8] pt-16 md:pt-20" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8EA696]">Admin console</p>
            <h1 className="font-display font-light text-3xl text-[#0A0A0A] mt-1">Edit the website</h1>
          </div>
          <button type="button" onClick={logout} data-testid="admin-logout"
            className="text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A] border border-[#E5E7E3] px-5 py-2.5 hover:border-[#11261A] hover:text-[#11261A] transition-colors">
            Sign out
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`rounded-full text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 transition-colors ${
                tab === t.id ? "bg-[#11261A] text-[#F9FAF8]" : "bg-white border border-[#E5E7E3] text-[#4A4A4A] hover:border-[#11261A]"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="bg-[#F9FAF8]">
          <Active token={token} />
        </div>
      </div>
    </div>
  );
}
